import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';

import DatePicker from './DatePicker';
import Svg from '../../../components/react/Svg';

const DateInputGroup = ({
  startDateName,
  startDateLabel,
  startDatePlaceholder,
  endDateName,
  endDateLabel,
  endDatePlaceholder,
  startDate,
  endDate,
  onChange
}) => {
  const ref = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState('top');
  const [focusedInput, setFocusedInput] = useState('start');

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) {
        return;
      }

      const { scrollY, innerHeight } = window;
      const { offsetTop } = ref.current;

      if (offsetTop - scrollY < innerHeight / 2) {
        setPosition('bottom');
      } else {
        setPosition('top');
      }
    };

    document.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => document.removeEventListener('scroll', handleScroll);
  }, [ref]);

  return (
    <div className="booking-widget__date-group" ref={ref}>
      <div
        className={classNames(
          'booking-widget__input',
          'booking-widget__input--date',
          { focused: focusedInput === 'start' && isOpen }
        )}
        onClick={() => {
          setIsOpen(focusedInput === 'end' ? true : !isOpen);
          setFocusedInput('start');
        }}
      >
        <input
          type="date"
          name={startDateName}
          value={moment(startDate).format('YYYY-MM-DD')}
          aria-label={startDateLabel}
          min={moment().format('YYYY-MM-DD')}
          onChange={e => onChange(moment(e.target.value), endDate)}
        />
        <span className="booking-widget__date-placeholder">
          {startDatePlaceholder}
          {startDate ? `: ${startDate.format('LL')}` : null}
        </span>
        <Svg viewBox="0 0 20 20" name="booking-calendar" className="booking-widget__input-icon" altText="pick a date"/>
      </div>
      <div
        className={classNames(
          'booking-widget__input',
          'booking-widget__input--date',
          { focused: focusedInput === 'end' && isOpen }
        )}
        onClick={() => {
          setIsOpen(startDate && focusedInput === 'start' ? true : !isOpen);
          setFocusedInput(startDate ? 'end' : 'start');
        }}
      >
        <input
          type="date"
          name={endDateName}
          value={moment(endDate).format('YYYY-MM-DD')}
          aria-label={endDateLabel}
          min={moment(startDate).format('YYYY-MM-DD')}
          onChange={e => onChange(startDate, moment(e.target.value))}
        />
        <span className="booking-widget__date-placeholder">
          {endDatePlaceholder}
          {endDate ? `: ${endDate.format('LL')}` : null}
        </span>
        <Svg viewBox="0 0 20 20" name="booking-calendar" className="booking-widget__input-icon" altText="pick a date"/>
      </div>
      {isOpen && (
        <DatePicker
          position={position}
          startDateLabel={startDateLabel}
          endDateLabel={endDateLabel}
          startDate={startDate}
          endDate={endDate}
          focusedInput={focusedInput}
          onFocusChange={focus => setFocusedInput(focus)}
          onSelectDate={onChange}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

DateInputGroup.propTypes = {
  startDateName: PropTypes.string.isRequired,
  startDateLabel: PropTypes.string.isRequired,
  startDatePlaceholder: PropTypes.string.isRequired,
  endDateName: PropTypes.string.isRequired,
  endDateLabel: PropTypes.string.isRequired,
  endDatePlaceholder: PropTypes.string.isRequired,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  onChange: PropTypes.func.isRequired
};

export default DateInputGroup;
